import axios from "axios";
import jwtDecode, { JwtPayload } from "jwt-decode";
import React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { withRouter } from "react-router-dom";

import { HttpError } from "@/browser";
import { VALIDATION_TYPES } from "@/components/Formik";
import { errorEntryType, hasCode } from "@/core/api";
import {
  API as SessionAPI,
  getAuthToken,
  getLoginOnBehalfToken,
  ISessionUser,
  loginOnBehalf,
  logout,
  refresh,
  STORAGE_KEY_TOKEN,
} from "@/data/session";
import { IWithMessagesActions, withMessagesActions } from "@/eworkitComponents";
import * as PubSub from "@/infrastructure/PubSub";
import { IWithRouteMatchProps } from "@/infrastructure/Route";
import { IWithSessionProps, withSession } from "@/infrastructure/Session";
import { hasValue } from "@/utils/objects";
import { router } from "@/views/router";
import { FORM_MESSENGER_CONTEXT } from "@/views/Session/Login/components";

const FIVE_DAYS_IN_SECONDS = 5 * 24 * 60 * 60;

interface Props extends IWithMessagesActions, IWithRouteMatchProps, IWithSessionProps<ISessionUser>, WithTranslation {}

class SessionResolverComponent extends React.Component<Props> {
  requestFailureError: string;
  refreshInterceptorId: number;
  isRefreshing = false;

  async componentDidMount() {
    const { authenticated } = this.props.session;
    const token = getAuthToken();
    const loginOnBehalfToken = getLoginOnBehalfToken();

    if (!authenticated) {
      if (loginOnBehalfToken) {
        await this.tryLoginOnBehalf(loginOnBehalfToken);
      } else if (token) {
        await this.tryAuthenticate();
      }

      this.markAsResolved();
    }

    this.registerRefreshInterceptor();
    this.addTokenChangeListener();
  }

  componentWillUnmount() {
    axios.interceptors.request.eject(this.refreshInterceptorId);
    this.removeTokenChangeListener();
    PubSub.unsubscribe(this.requestFailureError);
  }

  async tryLoginOnBehalf(token: string) {
    try {
      const sessionUser = await loginOnBehalf({ token });
      this.props.session.create(sessionUser);
    } catch {
      await this.tryAuthenticate();
      this.props.addMessage({
        text: this.props.t("SESSION_LOGIN_ON_BEHALF.INVALID_TOKEN"),
        type: "warning",
        lifeTime: Infinity,
      });
    }
  }

  async tryAuthenticate() {
    try {
      this.requestFailureError = PubSub.subscribe("requests.failure", this.handleRequestFailure);
      const sessionUser = await refresh();
      this.props.session.create(sessionUser);
    } catch {
      await this.clearSession();
    }
  }

  async updateSession() {
    try {
      const sessionUser = await refresh();
      this.props.session.update(sessionUser);
    } catch {
      await this.clearSession();
    }
  }

  clearSession = async () => {
    const {
      session: { destroy },
      location: { pathname, search },
    } = this.props;

    try {
      await logout();
    } catch {
      if (/\/app/.test(pathname)) {
        this.props.history.push(router.get("login").path, { redirect: pathname + search });
      }
    }
    destroy();
  };

  markAsResolved = () => {
    if (this.props.session.pristine) {
      this.props.session.markAsPristine(false);
    }
  };

  registerRefreshInterceptor() {
    this.refreshInterceptorId = axios.interceptors.request.use(async request => {
      const token = getAuthToken();

      if (token && !this.isRefreshing && !hasValue(SessionAPI, request.url)) {
        const decodedToken = jwtDecode<JwtPayload>(token);
        const currentTime = Math.floor(Date.now() / 1000);
        const secondsUntilTokenExpires = decodedToken.exp ? decodedToken.exp - currentTime : 0;

        if (secondsUntilTokenExpires < FIVE_DAYS_IN_SECONDS) {
          try {
            this.requestFailureError = PubSub.subscribe("requests.failure", this.handleRequestFailure);
            this.isRefreshing = true;
            await refresh();
          } catch {
            await this.clearSession();
          } finally {
            this.isRefreshing = false;
          }
        }
      }

      return request;
    });
  }

  addTokenChangeListener = () => {
    window.addEventListener("storage", this.handleTokenChange, false);
  };

  removeTokenChangeListener = () => {
    window.removeEventListener("storage", this.handleTokenChange);
  };

  handleTokenChange = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY_TOKEN) {
      const { newValue, oldValue } = event;

      if (!oldValue && newValue) {
        this.updateSession();
      }

      if (oldValue && !newValue) {
        this.props.session.destroy();
      }
    }
  };

  handleRequestFailure = (data: HttpError) => {
    const payload = errorEntryType(data.payload);

    if (hasCode(payload, VALIDATION_TYPES.TOKEN_EXPIRED)) {
      this.handleTokenExpired();
    }
  };

  handleTokenExpired = async () => {
    const {
      t,
      session: { destroy },
      location: { pathname, search },
    } = this.props;

    this.props.addMessage({
      text: t("SESSION_LOGOUT.EXPIRED_SESSION"),
      type: "warning",
      context: FORM_MESSENGER_CONTEXT,
      lifeTime: Infinity,
    });

    if (/\/app/.test(pathname)) {
      this.props.history.push(router.get("login").path, { redirect: pathname + search });
      destroy();
    }
  };

  render() {
    return !this.props.session.pristine && this.props.children;
  }
}

export const SessionResolver = withRouter(
  withMessagesActions(withSession(withTranslation("public")(SessionResolverComponent))),
);
